import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"
import { useLocalization } from "@tm/localization"
import { Note, NoteTypes } from "@tm/models"
import { FormControl, Stack, styled } from "@mui/material"
import { TextField } from "../../../muiStyled/Inputs/textfield"
import { Icon } from "../../../muiStyled/DataDisplay/Icons"
import { Tooltip } from "../../../muiStyled/DataDisplay/tooltip"
import { IconButton } from "../../../muiStyled/Inputs/button"

const MAXINPUTCHARS = 500

type NoteItemProps = {
    note: Note
    isLoading: boolean
    onDelete(noteId: string): unknown
    onSave(note: Note): unknown
}

const FormControlFullWidth = styled(FormControl)(() => ({
    width: "100%",
}))

const TextFieldWithInlineCounter = styled(TextField)(({ theme }) => ({
    marginTop: 0,
    width: "640px",
    ".MuiInputBase-root": {
        backgroundColor: theme.overwrites?.components?.textfield?.backgroundColor || "unset",
        "&:hover": {
            backgroundColor: theme.overwrites?.components?.textfield?.backgroundColor || "unset",
        },
        "&.Mui-focused": {
            backgroundColor: theme.overwrites?.components?.textfield?.backgroundColor || "unset",
        },
    },
    ".MuiInputBase-multiline": {
        paddingTop: 8,
    },
}))

export function TileNoteItem(props: NoteItemProps) {
    const {
        note: { noteId, text, type, version },
        onDelete,
        onSave,
        isLoading,
    } = props

    const { translateText } = useLocalization()
    const [value, setValue] = useState<string>(text ?? "")

    useEffect(
        function setLoadedText() {
            setValue(text ?? "")
        },
        [text]
    )

    const handleOnInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setValue(event.target.value)
    }

    const handleSave = useCallback(() => onSave({ noteId, text: value, type, version }), [noteId, onSave, type, value, version])
    const handleDelete = useCallback(() => onDelete(noteId), [noteId, onDelete])

    const iconName = useMemo(() => (type === NoteTypes.VEHICLE_ARTICLE ? "note-vehicle" : "note-article"), [type])
    const placeholder = useMemo(() => translateText(type === NoteTypes.VEHICLE_ARTICLE ? 14058 : 14059), [translateText, type])
    const label = useMemo(() => translateText(type === NoteTypes.VEHICLE_ARTICLE ? 12875 : 12874), [translateText, type])

    return (
        <>
            <Tooltip title={label} sx={{ paddingX: 1 }}>
                <Icon name={iconName} size="extralarge" />
            </Tooltip>
            <FormControlFullWidth>
                <TextFieldWithInlineCounter
                    id="component-note-text-small"
                    value={value}
                    label={label}
                    placeholder={placeholder}
                    // to show both label and placeholder at the same time
                    InputLabelProps={{ shrink: true }}
                    maxRows={5}
                    size="large"
                    multiline
                    fullWidth
                    inputCounter
                    inputProps={{
                        maxLength: MAXINPUTCHARS,
                    }}
                    onChange={handleOnInputChange}
                />
            </FormControlFullWidth>
            <Stack direction="row" gap={0.5} alignItems="center">
                <Tooltip title={translateText(69)}>
                    <IconButton variant="contained" size="extralarge" onClick={handleDelete} disabled={isLoading || !text}>
                        <Icon name="delete" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={translateText(9)}>
                    <IconButton
                        variant="contained"
                        size="extralarge"
                        color="success"
                        onClick={handleSave}
                        disabled={isLoading || !value || value === text}
                    >
                        <Icon name="check" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </>
    )
}
